import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import expertIcon from '@/assets/Region/expert.svg';
import toursTastingsIcon from '@/assets/Region/tours-tastings.svg';
import region1Image from '@/assets/Region/region1.webp';
import region2Image from '@/assets/Region/region2.webp';
import region3Image from '@/assets/Region/region3.webp';
import CustomButton from '@/components/Button/button';
import Section from '@/components/Section/section';
import { useInView } from '@/hooks/useInView';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';
import { fetchFeaturedVineyards } from '@/services/api/vineyardService';

const CARD_STAGGER_MS = 130;

const DESCRIPTION =
  "From well-known estates to hidden gems, we'll help you uncover the best places to taste, tour, stay and shop - whether you're planning a countryside weekend or simply curious to learn more.";

interface RegionFeatureTag {
  label: string;
  key: string;
  kind: 'type' | 'experience';
}

const TOURS_TASTINGS_TAG: RegionFeatureTag = {
  label: 'Tours & Tastings',
  key: 'tours_tastings',
  kind: 'experience',
};

const STYLE_TAG_ORDER = ['style_still', 'style_sparkling'];

const TYPE_TAG_KEYS = new Set([
  'sparkling',
  'still',
  'style_sparkling',
  'style_still',
  'red',
  'white',
  'rose',
  'orange',
  'dessert',
  'fortified',
  'natural',
  'organic',
  'vegan',
  'biodynamic',
]);

interface RegionCard {
  vineyard_id: string;
  tag: string;
  tagColor: string;
  tagBorderColor: string;
  title: string;
  subtitle: string;
  paragraph1: string;
  paragraph2?: string;
  featureTags: RegionFeatureTag[];
  image: string;
  rating: number;
  reviewCount: number;
  url: string;
  address: string;
  phone: string;
  location: { lat: number; lng: number };
  priceRange: string;
  metaDescription: string;
}

// Placeholder REGIONS for fallback
const FALLBACK_REGIONS: RegionCard[] = [
  {
    vineyard_id: 'fallback-1',
    tag: 'Somerset',
    tagColor: color.merlot['400'],
    tagBorderColor: color.merlot['300'],
    title: 'Wraxall Vineyard',
    subtitle: 'Somerset | First Class English Wines',
    paragraph1:
      'Wraxall Vineyard is an established, award-winning Somerset vineyard near Shepton Mallet, planted in 1974 on a scenic south-facing hill.',
    paragraph2:
      'Visitors can experience panoramic countryside views, vineyard tours, and tastings at the View@Wraxall, which features a stunning wrap-around terrace overlooking the valley.',
    featureTags: [
      TOURS_TASTINGS_TAG,
      { label: 'Award winning', key: 'award_winning', kind: 'experience' },
    ],
    metaDescription:
      'Visit Wraxall Vineyard in Somerset for award-winning wine tours and tastings. Experience panoramic views at View@Wraxall. Plan your visit with UK Vineyard Guide.',
    image: region1Image,
    rating: 4.9,
    reviewCount: 52,
    url: 'https://www.ukvineyardguide.co.uk/regions/wraxall',
    address: 'Wraxall, Shepton Mallet, Somerset BA4 6RN, UK',
    phone: '+44 7508 051209',
    location: { lat: 51.1345, lng: -2.5712 },
    priceRange: '££',
  },
  {
    vineyard_id: 'fallback-2',
    tag: 'Kent',
    tagColor: color.coral['300'],
    tagBorderColor: color.coral['200'],
    title: 'Balfour Winery',
    subtitle: 'Kent | Premium English Sparkling Wine',
    paragraph1:
      'Balfour Winery, located on the 400-acre Hush Heath Estate in Kent, is a renowned producer of world-class English sparkling and still wines.',
    paragraph2:
      'Just 50 minutes from London, the estate offers a modern visitor centre, guided tours through ancient woodlands and orchards, and expert-led tutored tastings.',
    featureTags: [
      TOURS_TASTINGS_TAG,
      { label: 'Award winning', key: 'award_winning', kind: 'experience' },
    ],
    metaDescription:
      'Discover Balfour Winery at Hush Heath Estate, Kent. Book luxury wine tours, tastings, and explore 400 acres of vineyards. Guided by UK Vineyard Guide.',
    image: region2Image,
    rating: 5.0,
    reviewCount: 124,
    url: 'https://www.ukvineyardguide.co.uk/regions/balfour',
    address: 'Five Oak Lane, Staplehurst, Tonbridge, Kent TN12 0HT, UK',
    phone: '+44 1622 832794',
    location: { lat: 51.1552, lng: 0.5124 },
    priceRange: '££-£££',
  },
  {
    vineyard_id: 'fallback-3',
    tag: 'Sussex',
    tagColor: color.olive['400'],
    tagBorderColor: color.olive['300'],
    title: 'Ambriel',
    subtitle: 'West Sussex | Premium English Sparkling Wine',
    paragraph1:
      'Ambriel is a family-owned, single-estate vineyard in West Sussex, dedicated to crafting exceptional sparkling wines from traditional Champagne varieties.',
    paragraph2:
      'Set on south-facing slopes with rare Greensand soil, they focus on sustainable viticulture to produce vintage-only cuvées of remarkable character.',
    featureTags: [
      TOURS_TASTINGS_TAG,
      { label: 'Award winning', key: 'award_winning', kind: 'experience' },
    ],
    metaDescription:
      'Experience Ambriel Sparkling in West Sussex. Boutique vineyard tours and premium sparkling wine tastings. Book your Sussex wine experience today.',
    image: region3Image,
    rating: 4.8,
    reviewCount: 38,
    url: 'https://www.ukvineyardguide.co.uk/regions/ambriel',
    address:
      'Redfold Vineyards, Nutbourne Lane, Pulborough, West Sussex RH20 2HS, UK',
    phone: '+44 1798 813657',
    location: { lat: 50.9575, lng: -0.4952 },
    priceRange: '££',
  },
] as const;

interface RegionSectionProps {
  onDownloadClick: () => void;
}

export default function RegionSection({
  onDownloadClick,
}: RegionSectionProps): JSX.Element {
  const [sectionRef, inView] = useInView({ threshold: 0.2 });
  const [regions, setRegions] = useState<RegionCard[]>(FALLBACK_REGIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [, setTouchEnd] = useState<number | null>(null);
  const [desktopPage, setDesktopPage] = useState(0);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 899px)').matches
      : false
  );
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const desktopTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch featured vineyards on mount
  useEffect(() => {
    let isMounted = true;

    const loadVineyards = async () => {
      try {
        setIsLoading(true);
        const vineyards = await fetchFeaturedVineyards();

        if (isMounted) {
          // Transform API data to RegionCard format
          const transformedRegions = vineyards.map((vineyard) => {
            const normalizedTags: RegionFeatureTag[] = vineyard.tags
              .filter(
                (t) =>
                  t.label &&
                  t.key !== 'child_friendly' &&
                  t.label.toLowerCase() !== 'child friendly'
              )
              .map((t) => ({
                label: t.label,
                key: t.key,
                kind:
                  TYPE_TAG_KEYS.has(t.key) || t.key.startsWith('style_')
                    ? ('type' as const)
                    : ('experience' as const),
              }));

            const styleTags = normalizedTags.filter((t) =>
              t.key.startsWith('style_')
            );
            const firstExperienceTag = normalizedTags.find(
              (t) => t.kind === 'experience'
            );

            const orderedStyleTags = [...styleTags].sort((a, b) => {
              const aIndex = STYLE_TAG_ORDER.indexOf(a.key);
              const bIndex = STYLE_TAG_ORDER.indexOf(b.key);

              if (aIndex === -1 && bIndex === -1) return 0;
              if (aIndex === -1) return 1;
              if (bIndex === -1) return -1;

              return aIndex - bIndex;
            });

            const uniqueStyleLabels = Array.from(
              new Set(orderedStyleTags.map((t) => t.label.trim()))
            );

            const combinedStyleLabel = uniqueStyleLabels.join(' & ');

            const featureTags: RegionFeatureTag[] = [TOURS_TASTINGS_TAG];

            if (combinedStyleLabel) {
              featureTags.push({
                label: combinedStyleLabel,
                key: 'style_combined',
                kind: 'type',
              });
            } else if (firstExperienceTag) {
              featureTags.push(firstExperienceTag);
            } else {
              featureTags.push({
                label: 'Featured',
                key: 'featured',
                kind: 'experience',
              });
            }

            return {
              vineyard_id: vineyard.id,
              tag: vineyard.region,
              tagColor: vineyard.regionColor,
              tagBorderColor: vineyard.regionColor,
              title: vineyard.name,
              subtitle: `${vineyard.region} | Featured Vineyard`,
              paragraph1: vineyard.description,
              paragraph2: '',
              featureTags,
              image: vineyard.coverImageUrl || '',
              rating: vineyard.averageRating ?? 0,
              reviewCount: 0,
              url: `https://www.ukvineyardguide.co.uk/vineyard/${vineyard.id}`,
              address: '',
              phone: '',
              location: {
                lat: vineyard.latitude,
                lng: vineyard.longitude,
              },
              priceRange: '£-£££',
              metaDescription: `${vineyard.name} in ${vineyard.region}. ${vineyard.description?.substring(0, 100) || ''}`,
            } as RegionCard;
          });

          setRegions(
            transformedRegions.length > 0
              ? transformedRegions
              : FALLBACK_REGIONS
          );
          setDesktopPage(0);
        }
      } catch {
        if (isMounted) {
          // Use fallback regions on error
          setRegions(FALLBACK_REGIONS);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadVineyards();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Track section view
  useEffect(() => {
    if (inView) {
      gtag('event', 'section_view', {
        event_category: 'Engagement',
        event_label: 'Region Section',
      });
    }
  }, [inView]);

  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX ?? null);
  };

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentCardIndex((prev) => (prev + 1) % regions.length);
    }, 10000);
  }, [regions.length]);

  const startDesktopTimer = useCallback((pageCount: number) => {
    if (desktopTimerRef.current) clearInterval(desktopTimerRef.current);
    desktopTimerRef.current = setInterval(() => {
      setDesktopPage((prev) => (prev + 1) % pageCount);
    }, 10000);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isMobile, startTimer]);

  useEffect(() => {
    if (isMobile || isLoading || regions.length <= 3) {
      if (desktopTimerRef.current) clearInterval(desktopTimerRef.current);
      return;
    }
    startDesktopTimer(regions.length);
    return () => {
      if (desktopTimerRef.current) clearInterval(desktopTimerRef.current);
    };
  }, [isMobile, isLoading, regions.length, startDesktopTimer]);

  const goToCard = useCallback(
    (index: number) => {
      setCurrentCardIndex(index);
      if (isMobile) startTimer();
    },
    [isMobile, startTimer]
  );

  const goToDesktopPage = useCallback(
    (index: number) => {
      setDesktopPage(index);
      startDesktopTimer(regions.length);
    },
    [regions.length, startDesktopTimer]
  );

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart == null) return;

    const clientX = e.changedTouches[0]?.clientX;
    if (clientX == null) return;

    setTouchEnd(clientX);

    const distance = touchStart - clientX;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToCard((currentCardIndex + 1) % regions.length);
    } else if (isRightSwipe) {
      goToCard((currentCardIndex - 1 + regions.length) % regions.length);
    }
  };

  const isDesktopCarousel = !isLoading && regions.length > 3;
  // One step per vineyard so each gets to be the first visible card
  const desktopPageCount = regions.length;
  // The 3 indices to show on desktop at the current rotation step
  const desktopVisibleIndices = isDesktopCarousel
    ? [0, 1, 2].map((offset) => (desktopPage + offset) % regions.length)
    : null;

  return (
    <>
      <Helmet>
        {regions.map((region) => (
          <>
            {/* LocalBusiness Schema with Complete Details */}
            <script
              key={`schema-${region.vineyard_id}`}
              type="application/ld+json"
            >
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'LocalBusiness',
                '@id': region.url,
                name: region.title,
                url: region.url,
                image: region.image,
                description: region.paragraph1,
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: region.address,
                  addressCountry: 'GB',
                },
                telephone: region.phone,
                priceRange: region.priceRange,
                geo: {
                  '@type': 'GeoCoordinates',
                  latitude: region.location.lat,
                  longitude: region.location.lng,
                },
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: region.rating,
                  reviewCount: region.reviewCount,
                  bestRating: '5',
                  worstRating: '1',
                },
                hasOfferCatalog: {
                  '@type': 'OfferCatalog',
                  name: `${region.title} Experiences`,
                  itemListElement: [
                    {
                      '@type': 'Offer',
                      itemOffered: {
                        '@type': 'Service',
                        name: 'Vineyard Tours',
                      },
                    },
                    {
                      '@type': 'Offer',
                      itemOffered: {
                        '@type': 'Service',
                        name: 'Wine Tastings',
                      },
                    },
                  ],
                },
              })}
            </script>

            {/* Meta Description */}
            <meta
              key={`desc-${region.vineyard_id}`}
              name="description"
              content={region.metaDescription}
            />
            <link
              key={`canonical-${region.vineyard_id}`}
              rel="canonical"
              href={region.url}
            />

            {/* Open Graph Tags */}
            <meta
              key={`og-title-${region.vineyard_id}`}
              property="og:title"
              content={`${region.title} | ${region.tag} Vineyard`}
            />
            <meta
              key={`og-desc-${region.vineyard_id}`}
              property="og:description"
              content={region.metaDescription}
            />
            <meta
              key={`og-url-${region.vineyard_id}`}
              property="og:url"
              content={region.url}
            />
            <meta
              key={`og-image-${region.vineyard_id}`}
              property="og:image"
              content={region.image}
            />
            <meta
              key={`og-type-${region.vineyard_id}`}
              property="og:type"
              content="website"
            />

            {/* Twitter Card Tags */}
            <meta
              key={`twitter-card-${region.vineyard_id}`}
              name="twitter:card"
              content="summary_large_image"
            />
            <meta
              key={`twitter-title-${region.vineyard_id}`}
              name="twitter:title"
              content={`${region.title} | ${region.tag} Vineyard Tours`}
            />
            <meta
              key={`twitter-desc-${region.vineyard_id}`}
              name="twitter:description"
              content={region.metaDescription}
            />
            <meta
              key={`twitter-image-${region.vineyard_id}`}
              name="twitter:image"
              content={region.image}
            />
          </>
        ))}
      </Helmet>
      <Section
        ref={sectionRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'white',
          py: { xs: '48px', md: '64px' },
          px: { xs: '24px', md: '64px' },
          '@keyframes regionHeadingIn': {
            '0%': { opacity: 0, transform: 'translateY(-24px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes regionCardIn': {
            from: { opacity: 0, transform: 'translateY(22px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes dotProgress': {
            '0%': { width: '0%' },
            '100%': { width: '100%' },
          },
        }}
      >
        <Typography
          component="h2"
          sx={{
            ...typography.display,
            color: color.charcoal.default,
            textAlign: 'center',
            marginBottom: '32px',
            fontSize: {
              xs: 'var(--font-size-36, 36px)',
              sm: 'var(--font-size-48, 48px)',
              md: typography.display.fontSize,
            },
          }}
        >
          Regions to Explore
        </Typography>
        <Typography
          sx={{
            ...typography.heading4,
            color: color.charcoal.default,
            textAlign: 'center',
            fontWeight: 350,
            lineHeight: 1.25,
            maxWidth: '760px',
            mb: { xs: '28px', md: '64px' },
            fontSize: {
              xs: 'var(--font-size-18, 18px)',
              sm: 'var(--font-size-20, 20px)',
              md: typography.heading4.fontSize,
            },
          }}
        >
          {DESCRIPTION}
        </Typography>

        <Box
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            display: { xs: 'flex', md: 'grid' },
            gridTemplateColumns: {
              md: 'repeat(3, 1fr)',
            },
            flexDirection: { xs: 'column', md: 'unset' },
            gap: { xs: '20px', md: '16px' },
            width: '100%',
            maxWidth: '1200px',
            mb: { xs: '28px', md: isDesktopCarousel ? '24px' : '44px' },
            touchAction: 'pan-y',
          }}
        >
          {isLoading
            ? // Skeleton loaders
              Array.from({ length: 3 }).map((_, i) => (
                <Box
                  key={`skeleton-${i}`}
                  sx={{
                    backgroundColor: 'white',
                    boxShadow: '0 0 12px #0000001A',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    p: '10px',
                    pb: '32px',
                    display: {
                      xs: i === currentCardIndex ? 'block' : 'none',
                      md: 'block',
                    },
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      mb: '10px',
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      sx={{
                        width: '100%',
                        height: '200px',
                        borderRadius: '10px',
                      }}
                    />
                  </Box>
                  <Box sx={{ px: '8px', pb: '8px' }}>
                    <Skeleton
                      variant="text"
                      sx={{ mb: '12px', height: '28px' }}
                    />
                    <Skeleton
                      variant="text"
                      sx={{ mb: '16px', height: '20px' }}
                    />
                    <Skeleton
                      variant="text"
                      sx={{ mb: '12px', height: '16px' }}
                    />
                    <Skeleton
                      variant="text"
                      sx={{ mb: '16px', height: '16px' }}
                    />
                    <Skeleton
                      variant="rectangular"
                      sx={{
                        borderRadius: '100px',
                        height: '48px',
                        width: '100%',
                      }}
                    />
                  </Box>
                </Box>
              ))
            : // Actual content
              regions.map((region, i) => (
                <Box
                  key={region.vineyard_id}
                  sx={{
                    backgroundColor: 'white',
                    boxShadow: '0 0 12px #0000001A',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    p: '10px',
                    pb: '32px',
                    display: {
                      xs: i === currentCardIndex ? 'flex' : 'none',
                      md: isDesktopCarousel
                        ? desktopVisibleIndices!.includes(i)
                          ? 'flex'
                          : 'none'
                        : 'flex',
                    },
                    order: isDesktopCarousel
                      ? desktopVisibleIndices!.indexOf(i) !== -1
                        ? desktopVisibleIndices!.indexOf(i)
                        : 99
                      : 'unset',
                    flexDirection: 'column',
                    opacity: { xs: 1, md: isDesktopCarousel ? 1 : 0 },
                    ...(inView &&
                      !isDesktopCarousel && {
                        animation: 'regionCardIn 0.55s ease-out forwards',
                        animationDelay: `${i * CARD_STAGGER_MS}ms`,
                      }),
                    '@media (max-width: 899px)': {
                      animation: 'none',
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      mb: '16px',
                      position: 'relative',
                      height: '343px',
                    }}
                  >
                    <Box
                      component="img"
                      src={region.image}
                      alt={`${region.title} ${region.subtitle.split(' | ').join(' ')}`}
                      loading="lazy"
                      sx={{
                        width: '100%',
                        height: '343px',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '10px',
                        top: '10px',
                        minWidth: '122px',
                        borderRadius: '100px',
                        py: '6px',
                        px: '12px',
                        backgroundColor: region.tagColor,
                        border: `1px solid ${region.tagBorderColor}`,
                        color: color.cream['100'],
                        textAlign: 'center',
                        ...typography.bodyTextBold,
                      }}
                    >
                      {region.tag}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      px: '8px',
                      justifyContent: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      flex: 1,
                    }}
                  >
                    <Typography
                      component="h3"
                      sx={{
                        ...typography.heading4,
                        color: color.charcoal.default,
                        fontFamily: 'Gotham',
                        fontWeight: 400,
                        textAlign: 'center',
                        mb: '12px',
                      }}
                    >
                      {region.title}
                    </Typography>
                    <Typography
                      sx={{
                        ...typography.caption,
                        color: color.charcoal['400'],
                        fontWeight: 325,
                        textAlign: 'center',
                        mb: '16px',
                        fontSize: {
                          xs: 'var(--font-size-16, 14px)',
                          sm: 'var(--font-size-18, 18px)',
                          md: typography.caption.fontSize,
                        },
                      }}
                    >
                      {region.subtitle}
                    </Typography>
                    <Typography
                      sx={{
                        ...typography.caption,
                        color: color.charcoal['400'],
                        fontWeight: 300,
                        textAlign: 'center',
                        mb: '12px',
                        fontSize: {
                          xs: 'var(--font-size-12, 12px)',
                          sm: 'var(--font-size-12, 12px)',
                          md: 'var(--font-size-12, 12px)',
                        },
                        lineHeight: 1.5,
                      }}
                    >
                      {region.paragraph1}
                    </Typography>
                    <Typography
                      sx={{
                        ...typography.caption,
                        color: color.charcoal['400'],
                        fontWeight: 300,
                        textAlign: 'center',
                        mb: '16px',
                        fontSize: {
                          xs: 'var(--font-size-12, 12px)',
                          sm: 'var(--font-size-12, 12px)',
                          md: 'var(--font-size-12, 12px)',
                        },
                        lineHeight: 1.5,
                      }}
                    >
                      {region.paragraph2}
                    </Typography>
                    <Box
                      sx={{
                        borderRadius: '100px',
                        backgroundColor: color.charcoal['100'],
                        py: '10px',
                        px: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        flexWrap: 'wrap',
                        mt: 'auto',
                      }}
                    >
                      {region.featureTags.map((featureTag) => (
                        <Box
                          key={`${region.vineyard_id}-${featureTag.key}`}
                          sx={{
                            borderRadius: '100px',
                            px: '10px',
                            py: '5px',
                            backgroundColor: 'transparent',
                            border: '1px solid transparent',
                          }}
                        >
                          <Typography
                            sx={{
                              ...typography.caption,
                              color: color.charcoal['500'],
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              lineHeight: 1,
                            }}
                          >
                            {featureTag.key === TOURS_TASTINGS_TAG.key && (
                              <Box
                                component="img"
                                src={toursTastingsIcon}
                                alt=""
                                aria-hidden
                                sx={{
                                  width: 14,
                                  height: 13,
                                  display: 'block',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {featureTag.kind === 'type' && (
                              <Box
                                component="img"
                                src={expertIcon}
                                alt=""
                                aria-hidden
                                sx={{
                                  width: 14,
                                  height: 14,
                                  display: 'block',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {featureTag.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ))}
        </Box>

        {/* Desktop Carousel Navigation */}
        {isDesktopCarousel && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              mb: '44px',
            }}
          >
            {Array.from({ length: desktopPageCount }).map((_, idx) => (
              <Box
                key={`desktop-dot-${idx}`}
                role="button"
                tabIndex={0}
                aria-label={`Go to page ${idx + 1}`}
                aria-pressed={idx === desktopPage}
                onClick={() => goToDesktopPage(idx)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goToDesktopPage(idx);
                  }
                }}
                sx={{
                  width: idx === desktopPage ? '40px' : '8px',
                  height: '8px',
                  borderRadius: '100px',
                  backgroundColor: color.charcoal['200'],
                  cursor: 'pointer',
                  transition: 'width 0.3s ease',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {idx === desktopPage && (
                  <Box
                    key={desktopPage}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: '0%',
                      backgroundColor: color.terracotta['300'],
                      borderRadius: '100px',
                      animation: 'dotProgress 10s linear forwards',
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            gap: '8px',
            justifyContent: 'center',
            alignItems: 'center',
            mb: { xs: '28px', md: '0px' },
          }}
        >
          {regions.map((region, index) => (
            <Box
              key={`dot-${region.vineyard_id}`}
              role="button"
              tabIndex={0}
              aria-label={`Go to ${region.title} region`}
              aria-pressed={index === currentCardIndex}
              onClick={() => goToCard(index)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToCard(index);
                }
              }}
              sx={{
                width: index === currentCardIndex ? '40px' : '8px',
                height: '8px',
                borderRadius: '100px',
                backgroundColor: color.charcoal['200'],
                cursor: 'pointer',
                transition: 'width 0.3s ease',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {index === currentCardIndex && (
                <Box
                  key={currentCardIndex}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '0%',
                    backgroundColor: color.terracotta['300'],
                    borderRadius: '100px',
                    animation: 'dotProgress 10s linear forwards',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
        <CustomButton
          variant="contained"
          onClick={() => {
            gtag('event', 'cta_click', {
              event_category: 'Engagement',
              event_label: 'Explore now – Region Section',
            });
            onDownloadClick();
          }}
          padding="14px 28px"
        >
          Explore now
        </CustomButton>
      </Section>
    </>
  );
}
